<template>
  <div>
    <!-- Mesmo título interno da aba Excepcionalidade do Editar escala (ScheduleApprovalFlowTab). -->
    <ScheduleStepHeading title="Configurações de excepcionalidade" class="mb-6">
      <OutlineButton
        text="Salvar como modelo"
        width="auto"
        height="36px"
        data-testid="save-template-button"
        @click="isSaveTemplateModalOpen = true"
      />
      <OutlineButton
        text="Importar configurações"
        width="auto"
        height="36px"
        data-testid="import-exceptionality-button"
        @click="isImportModalOpen = true"
      />
    </ScheduleStepHeading>

    <ImportExceptionalityConfigModal
      ref="importModalRef"
      :modal-active="isImportModalOpen"
      :saved-template="savedTemplate"
      :company-id="currentCompanyId"
      @close="handleImportClose"
      @save="handleImportConfig"
    />

    <SaveApprovalTemplateModal
      :modal-active="isSaveTemplateModalOpen"
      :layers="previewLayers"
      :loading="isSavingTemplate"
      @close="isSaveTemplateModalOpen = false"
      @save="handleSaveTemplate"
    />

    <div
      class="flex items-center gap-2 px-3 py-2 border border-primary30 rounded bg-primary5 mb-4"
      data-testid="info-operation-role"
    >
      <v-icon :icon="mdiFormTextboxLock" size="18" class="text-primary30 shrink-0" />
      <span class="text-sm text-secondary50">
        Os papéis validadores {{ mandatoryRolesLabel }} são obrigatórios no fluxo de validação de
        excepcionalidade, e não podem ser deletados
      </span>
    </div>

    <ApprovalLayerCard
      :group="stepApprovalFlow.layers[0]"
      :show-errors="showErrors"
      data-testid="approval-layer-0"
      @update:group="(updated) => updateLayer(updated)"
    />

    <p v-if="errors.layers" class="text-red-500 text-sm mt-1">
      {{ errors.layers }}
    </p>

    <ApprovalFlowPreview v-if="previewLayers.length > 0" :layers="previewLayers" class="mt-4" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue'

import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiFormTextboxLock } from '@mdi/js'

import { useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { useApprovalFlowStore } from '@/pinia-store/useApprovalFlowStore'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { groupToApiLayers } from '@/utils/approval-flow-transformers'
import { useImportApprovalFlow } from '@/hooks/useImportApprovalFlow'
import ApprovalLayerCard from './components/ApprovalLayerCard.vue'
import ApprovalFlowPreview from './ApprovalFlowPreview.vue'
import ScheduleStepHeading from './ScheduleStepHeading.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ImportExceptionalityConfigModal from '@/components/designSystem/modals/ImportExceptionalityConfigModal/ImportExceptionalityConfigModal.vue'
import SaveApprovalTemplateModal from '@/components/designSystem/modals/SaveApprovalTemplateModal/SaveApprovalTemplateModal.vue'

import {
  LayerOperator,
  MANDATORY_VALIDATION_ROLES,
  VALIDATION_ROLE_LABELS
} from '@/types/approval-flow.types'
import type { ApprovalGroup } from '@/types/approval-flow.types'
import type { ApprovalLayerDetail } from '@/Network/Types/Responses/ApprovalFlowResponses'

defineProps<{
  errors: Record<string, string>
  onClearError: (field: string) => void
  showErrors?: boolean
}>()

const toast = useToast()
const { notifyError } = useErrorToast()
const { importConfig } = useImportApprovalFlow()
const store = (inject('createScheduleStore') ?? useCreateScheduleStore()) as ReturnType<
  typeof useCreateScheduleStore
>
const { stepApprovalFlow } = storeToRefs(store)
const approvalFlowStore = useApprovalFlowStore()
const companyStore = useCompanyStore()

const currentCompanyId = computed(() => {
  return companyStore.myCompanies[0]?.id
})

// Deriva do conjunto real de obrigatórios (varia com `enabledMandatoryRiskValidator`)
// em vez de citar os papéis em texto fixo, que mentiria com a flag desligada.
const mandatoryRolesLabel = computed(() =>
  MANDATORY_VALIDATION_ROLES.map((role) => VALIDATION_ROLE_LABELS[role]).join(' e ')
)

stepApprovalFlow.value.enabled = true

const importModalRef = ref<InstanceType<typeof ImportExceptionalityConfigModal> | null>(null)
const isImportModalOpen = ref(false)
const isSaveTemplateModalOpen = ref(false)
const isSavingTemplate = ref(false)
const savedTemplate = ref<{ id: number; name: string } | null>(null)

const handleImportConfig = async (payload: { source: 'template' | 'scale'; id: number }) => {
  const imported = await importConfig(payload)
  if (imported) {
    stepApprovalFlow.value.layers[0] = imported
    isImportModalOpen.value = false
    importModalRef.value?.onImportSuccess()
  } else {
    isImportModalOpen.value = false
  }
  savedTemplate.value = null
}

const handleImportClose = () => {
  isImportModalOpen.value = false
  savedTemplate.value = null
}

const handleSaveTemplate = async (payload: { name: string; companyId: number }) => {
  isSavingTemplate.value = true
  try {
    const layers = groupToApiLayers(stepApprovalFlow.value.layers[0])
    const created = await approvalFlowStore.createTemplate({
      name: payload.name,
      companyId: payload.companyId,
      layers
    })
    savedTemplate.value = { id: created.id, name: created.name }
    isSaveTemplateModalOpen.value = false
    isImportModalOpen.value = true
    toast.success('Modelo salvo com sucesso.')
  } catch (e) {
    notifyError(e, 'Não foi possível salvar o modelo de configuração.')
  } finally {
    isSavingTemplate.value = false
  }
}

function updateLayer(updated: ApprovalGroup) {
  stepApprovalFlow.value.layers[0] = updated
}

const previewLayers = computed<ApprovalLayerDetail[]>(() => {
  const apiLayers = groupToApiLayers(stepApprovalFlow.value.layers[0])
  return apiLayers.map((layer, idx) => ({
    id: idx,
    order: layer.order,
    operator: layer.operator ?? LayerOperator.AND,
    roles: (layer.roles ?? []).map((r, rIdx) => ({
      id: rIdx,
      role: r.role,
      weight: r.weight ?? rIdx + 1
    }))
  }))
})
</script>
